import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { refreshSessionOnce, setAuthHeadersProvider, setAuthRefreshHandler } from '@/shared/api/client';
import { decodeToken, msUntilExpiry } from '@/shared/lib/jwt';
import { parseClaimsList } from '@/shared/permissions/claims';
import { ensureFingerprint, getCachedFingerprint } from '@/shared/lib/fingerprint';
import { initPush } from '@/shared/lib/pushNotifications';
import { pushDestination } from '@/shared/lib/pushRouting';
import { STORAGE_KEYS } from '@/shared/lib/constants';
import { env } from '@/shared/lib/env';
import * as authApi from '@/features/auth/api';
import { md5 } from '@/shared/lib/md5';
import { clearPortalSession, loadPersistedClaims, persistClaims, persistPortalSession, setAuthMirror } from '@/shared/lib/auth-state';
import { router } from '@/app/router';
import type { AssociatedTenant, PortalAccessInfo, AuthEnvelope, AuthState, LoginPayload } from '@/features/auth/types';
import { setSessionPresent } from './sessionPresence';

import {
  setUser, leggiChiave, migratePersonalKeys, clearSession, writeKey,
} from '@/shared/lib/storage';

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  /** Applica un AuthEnvelope ottenuto da flussi alternativi (passkey, Google JWT). */
  loginWithEnvelope: (envelope: AuthEnvelope) => void;
  /** Cambio password obbligatorio al primo accesso (login → 409): cambia e rientra. */
  changePasswordAndLogin: (email: string, oldPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  isAuthenticated: boolean;
  /**
   * I claim della sessione — la lista da usare per **disegnare** (menu, ⌘K, gate di pagina).
   *
   * Non è `decoded.claims`: su hard refresh quello è `null` finché il refresh non risponde, e in
   * quella finestra un menu gatato si mostrerebbe intero. Qui c'è la copia persistita come ripiego.
   */
  claims: readonly string[];
  /**
   * `true` quando sappiamo *se* questa persona ha claim (dal token o dalla copia persistita).
   *
   * Serve a distinguere «non ce l'ha» da «non lo so ancora»: nel dubbio il menu mostra e la
   * guardia di pagina attende, invece di negare a chi ha titolo.
   */
  readyClaims: boolean;
  needsTenantSelection: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_BUFFER_MS = 60_000;
const MAX_SAFE_TIMEOUT = 2_147_483_647; // setTimeout max (~24.8 days); larger values fire immediately!

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const refresh = leggiChiave(STORAGE_KEYS.refreshToken);
    return { token: null, refreshToken: refresh, decoded: null, user: null, associatedTenants: [], portalAccesses: [] };
  });
  const qc = useQueryClient();
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyAuthResponse = useCallback((env: AuthEnvelope | null) => {
    if (!env?.success || !env.data) return;
    const info = env.data;
    const token = authApi.stripBearerPrefix(info.authorizationBearer);
    const refreshToken = authApi.stripBearerPrefix(info.authorizationRefreshBearer);
    /*
     * I claim funzionali NON sono nel JWT (sei claim tecnici e basta, vedi
     * `shared/permissions/claims.ts`): arrivano qui, in `claimsList`. Innestarli in
     * `decoded.claims` è il punto in cui tutta la gating del FE smette di essere inerte,
     * e vale sia al login sia al refresh perché entrambi passano da qui.
     */
    const base = decodeToken(token);
    const decoded = base ? { ...base, claims: parseClaimsList(info.claimsList) } : null;
    if (refreshToken) writeKey(STORAGE_KEYS.refreshToken, refreshToken);
    if (info.tenantId) writeKey(STORAGE_KEYS.tenantId, info.tenantId);
    /*
     * Il mirror e il marcatore si scrivono **qui**, non nell'effetto più sotto.
     *
     * Chi decide dove mandare l'utente appena entrato — il gestore del form di login e le
     * `beforeLoad` delle rotte — vive fuori da React e legge `auth-state`. L'effetto gira
     * dopo il commit del render, cioè dopo che quella decisione è già stata presa: il
     * risultato era un utente-cliente spedito nella home del gestionale, dove ogni lettura
     * gli torna 403. Un `setTimeout(0)` prima di navigare non basta a chiudere la finestra,
     * perché non c'è nessuna garanzia di ordine fra un timer e il flush degli effetti.
     *
     * Il dato qui è già completo e definitivo: pubblicarlo adesso toglie la corsa invece di
     * renderla più difficile da perdere. L'effetto resta per le transizioni che non passano
     * di qua (logout, rehydrate).
     */
    setAuthMirror({
      token,
      tenantId: info.tenantId ?? leggiChiave(STORAGE_KEYS.tenantId),
      claims: decoded?.claims ?? [],
    });
    /*
     * Chi guarda, dichiarato allo storage — e **qui**, per la stessa ragione del mirror: le chiavi
     * intestate a una persona (preferiti, preset, modelli, segnalibri, colonne chiuse) si leggono
     * già durante il primo render successivo, e un id che arriva in un effetto le farebbe leggere
     * una volta sotto il nome nudo, cioè quelle di chi ha usato questo browser prima.
     *
     * La migrazione gira subito dopo, ed è la parte che non si può rimandare: i valori scritti
     * prima che le chiavi avessero un intestatario stanno sotto il nome nudo, e senza questo
     * passaggio un utente si vedrebbe sparire preset e segnalibri che ha davvero composto.
     */
    setUser(decoded?.userId ?? decoded?.sub ?? null);
    migratePersonalKeys();
    const tenants: AssociatedTenant[] = info.associatedTenants ?? [];
    /*
     * L'appartenenza interna decide la **shell**: un ibrido (P8) parte nel gestionale, un cliente
     * puro nel portale. `associatedTenants` è vuota per costruzione per un account di solo portale,
     * quindi è il discriminante giusto — e arriva nello stesso payload dei token, prima di qualunque
     * chiamata.
     */
    persistPortalSession(decoded?.claims ?? [], tenants.length > 0);
    /*
     * E i claim per intero, per la stessa ragione per cui si persiste il marcatore: al primo render
     * di un reload il token non c'è ancora, e senza questa copia il menu comparirebbe largo — cioè
     * mostrerebbe per un istante le voci che il gating serve a nascondere. Vale per disegnare, non
     * per autorizzare (vedi `persistClaims`).
     */
    persistClaims(decoded?.claims ?? []);
    /*
     * Gli accessi di portale arrivano **nello stesso payload** dei token (login, refresh e
     * change-tenant). Tenerli in stato è ciò che permette a un ibrido, dentro il gestionale, di
     * sapere di avere anche un accesso cliente: la rotta del portale gli risponderebbe 403, quindi
     * senza questo dato non c'è modo di offrirgli il passaggio senza fargli sbagliare.
     */
    const accesses: PortalAccessInfo[] = info.portalAccesses ?? [];
    setState({
      token,
      refreshToken,
      decoded,
      user: token
        ? {
            id: info.id,
            email: info.email,
            firstName: info.firstName,
            lastName: info.lastName,
          }
        : null,
      associatedTenants: tenants,
      portalAccesses: accesses,
    });
  }, []);

  const doLogout = useCallback(async () => {
    try {
      if (state.token) await authApi.logout();
    } catch {
      // ignore — local cleanup is what matters
    }
    /*
     * Una sola chiamata al posto di tre rimozioni a mano: l'elenco delle chiavi di sessione sta in
     * `storage.ts`, così una chiave nuova non deve essere ricordata anche qui — che è il modo in cui
     * `portal-access-id` era rimasta fuori.
     *
     * ⚠️ Non è un `localStorage.clear()`: nella stessa origine vivono la coda delle timbrature non
     * inviate e le preferenze delle persone che usano questo browser.
     */
    clearSession();
    // Senza questa, il prossimo che entra da questo browser verrebbe mandato nel portale.
    clearPortalSession();
    // Sono dati di un'altra persona: il prossimo che entra non deve rivederli.
    qc.clear();
    setState({ token: null, refreshToken: null, decoded: null, user: null, associatedTenants: [], portalAccesses: [] });
  }, [state.token, qc]);

  /*
   * Rinnovo della sessione su richiesta (avvio, timer, `refresh()` del contesto). Passa dallo stesso
   * single-flight del client, che chiama `refreshForApi` qui sotto: se una lettura sta già
   * rinnovando — all'avvio succede sempre, vedi `bearerFor` in `shared/api/client.ts` — questo si
   * accoda a quel rinnovo invece di farne un secondo.
   */
  const doRefresh = useCallback(async () => {
    const stored = state.refreshToken ?? leggiChiave(STORAGE_KEYS.refreshToken);
    if (!stored) {
      // no refresh token → nothing to refresh, ensure clean state
      return;
    }
    await refreshSessionOnce();
  }, [state.refreshToken]);

  /**
   * Handler per il refresh-on-401 dell'HTTP client (BE v1.8.2). Ritorna il nuovo
   * access token, o null se il refresh fallisce — in quel caso facciamo logout, e
   * il successivo router.invalidate() porta l'utente al login (route guard).
   */
  const refreshForApi = useCallback(async (): Promise<string | null> => {
    const stored = state.refreshToken ?? leggiChiave(STORAGE_KEYS.refreshToken);
    if (!stored) {
      await doLogout();
      return null;
    }
    try {
      // Il refresh token è del superadmin: rinnovare chiude l'impersonazione.
      const wasImpersonating = Boolean((state.decoded as Record<string, unknown> | null)?.impersonated_by);
      const resp = await authApi.refreshAccessToken(stored);
      applyAuthResponse(resp);
      if (wasImpersonating) qc.clear();
      return authApi.stripBearerPrefix(resp?.data?.authorizationBearer);
    } catch {
      await doLogout();
      return null;
    }
  }, [applyAuthResponse, doLogout, state.refreshToken, state.decoded, qc]);

  const doLogin = useCallback(
    async (email: string, password: string) => {
      const fingerprint = await ensureFingerprint();
      const lang = leggiChiave(STORAGE_KEYS.lang) ?? env.VITE_DEFAULT_LANG;
      // BE Identity accepts MD5 hex when user has IsPasswordMd5=1 (most accounts on dev).
      // For new accounts created via /register-otp, BE handles plain pwd. To keep client uniform,
      // we always pass MD5 — the BE accepts both shapes for legacy users and validates server-side.
      const hashed = md5(password);
      const payload: LoginPayload = { Email: email, Password: hashed, FingerPrint: fingerprint, UserLang: lang };
      const resp = await authApi.login(payload);
      applyAuthResponse(resp);
    },
    [applyAuthResponse]
  );

  const changePasswordAndLogin = useCallback(
    async (email: string, oldPassword: string, newPassword: string) => {
      // Il BE legge il device fingerPrint dall'header per il re-login interno: assicuriamoci
      // che sia in cache (di norma già impostato dal tentativo di login precedente).
      await ensureFingerprint();
      // old/new in MD5 come login e changePassword (setup.useMD5). La risposta è già
      // l'AuthEnvelope del re-login interno del BE → nessuna seconda login necessaria.
      const resp = await authApi.changePasswordFirstAccess(email, md5(oldPassword), md5(newPassword));
      applyAuthResponse(resp);
    },
    [applyAuthResponse]
  );

  /*
   * Il token al client in un layout effect, per la stessa ragione del gestore del rinnovo più
   * sotto: dopo un rinnovo, le query dei figli montati nello stesso commit partono prima degli
   * effetti di questo provider. Con un `useEffect` avrebbero visto ancora «nessun token» e,
   * trovando un refresh token salvato, avrebbero rinnovato una seconda volta.
   */
  useLayoutEffect(() => {
    const tenantId = state.decoded?.tenantId ?? leggiChiave(STORAGE_KEYS.tenantId) ?? null;
    setAuthHeadersProvider(() => ({
      token: state.token,
      fingerprint: getCachedFingerprint(),
      tenantId,
      lang: leggiChiave(STORAGE_KEYS.lang) ?? env.VITE_DEFAULT_LANG,
    }));
  }, [state.token, state.decoded]);

  useEffect(() => {
    const tenantId = state.decoded?.tenantId ?? leggiChiave(STORAGE_KEYS.tenantId) ?? null;
    // Update sync mirror used by route guards (beforeLoad).
    setAuthMirror({ token: state.token, tenantId, claims: state.decoded?.claims ?? [] });
    /* Il marcatore di portale NON si tocca da qui. Al reload lo stato si ricostruisce dal
       solo token, dove i claim funzionali non ci sono: riscriverlo con la lista vuota lo
       cancellerebbe, e il cliente si ritroverebbe nella shell interna al primo giro. Lo
       scrive `applyAuthResponse`, che è l'unico posto in cui i claim arrivano davvero. */
    // Invalidate router so active route guards re-run with the new state.
    void router.invalidate();
  }, [state.token, state.decoded]);

  /*
   * ESCI DA UNA SCHEDA, ESCI DA TUTTE.
   *
   * L'evento `storage` arriva **solo alle altre schede** della stessa origine, il che lo rende
   * esattamente lo strumento per questo: quando il refresh token viene rimosso — cioè quando
   * qualcuno ha premuto Esci da un'altra parte — questa scheda ha ancora il suo access token in
   * memoria e continuerebbe a funzionare fino alla scadenza. Su un PC condiviso è la differenza
   * fra «sono uscito» e «sono uscito da una finestra».
   *
   * ⚠️ Qui **non** si chiama `doLogout`: la sessione sul server l'ha già chiusa l'altra scheda, e
   * una seconda `logout()` con un token appena invalidato è solo un 401 nei log. Si azzera lo stato
   * locale e si rifà girare la guardia, che porta al login.
   *
   * `newValue === null` è il controllo che serve: un `storage` con un valore nuovo è un login o un
   * refresh in un'altra scheda, e quello non deve buttare fuori nessuno.
   */
  useEffect(() => {
    const onChange = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEYS.refreshToken || e.newValue !== null) return;
      setState({ token: null, refreshToken: null, decoded: null, user: null, associatedTenants: [], portalAccesses: [] });
      setUser(null);
      qc.clear();
      void router.invalidate();
    };
    window.addEventListener('storage', onChange);
    return () => window.removeEventListener('storage', onChange);
  }, [qc]);

  /*
   * Notifiche push: si registra il dispositivo **dopo l'accesso**, non all'avvio.
   *
   * Il dialogo di sistema che chiede il permesso si puo' mostrare una volta sola: mostrarlo sulla
   * schermata di login — prima che l'utente abbia visto a cosa serve — e' il modo piu' efficace di
   * farselo negare per sempre. Qui c'e' gia' un token, quindi c'e' gia' una ragione.
   *
   * Il token del dispositivo finisce sulla riga `user-devices` che porta il nostro fingerprint: e'
   * la stessa identita' che il BE registra al login. Sul web `initPush` non fa niente.
   */
  useEffect(() => {
    const userId = state.decoded?.userId ?? state.decoded?.sub ?? null;
    const fingerprint = getCachedFingerprint();
    if (!state.token || !userId || !fingerprint) return;
    void initPush({
      userId,
      fingerprint,
      /* Il tocco sulla notifica porta dove la notifica parla, quando si sa dove sia: il `data` del
         BE ha due sole chiavi (`pushType` e `pushData`, quest'ultima una stringa JSON) e otto tipi.
         Per quelli che non mappano non si naviga — restare dov'e' e ricaricare la campanella e'
         meglio che spostare qualcuno su una pagina sbagliata. Vedi `shared/lib/pushRouting`. */
      onOpen: (data) => {
        const dove = pushDestination(data);
        if (dove.route) void router.navigate({ href: dove.route });
      },
    });
  }, [state.token, state.decoded]);

  /*
   * Espone il rinnovo all'HTTP client (single-flight gestito lato client.ts).
   *
   * `useLayoutEffect`, non `useEffect`: le prime letture partono dagli effetti dei figli, che girano
   * **prima** degli effetti di questo provider ma **dopo** i suoi layout effect. Con un `useEffect`
   * la prima `/account/me` trovava il client senza gestore, e non poteva rinnovare prima di partire.
   */
  useLayoutEffect(() => {
    setAuthRefreshHandler(refreshForApi);
    return () => setAuthRefreshHandler(null);
  }, [refreshForApi]);

  /*
   * L'AZIENDA DEL JWT È CAMBIATA DAVVERO: si butta la cache.
   *
   * Le chiavi delle query non portano l'azienda, perché non serve: si sta sempre e solo nella
   * propria. Ma quando `change-tenant` ne consegna un'altra, le pagine già visitate
   * ripescherebbero le righe di quella di prima e le mostrerebbero mentre il refetch è in volo.
   * `clear()` e non `invalidateQueries()`: qui i dati sono di un'**altra società**, non
   * semplicemente vecchi.
   *
   * Si guarda il *cambio* fra due valori non nulli: al primo token della sessione (login, o boot
   * dal refresh token) non c'è niente da buttare.
   */
  const previousTenant = useRef<string | null>(null);
  useEffect(() => {
    const current = state.decoded?.tenantId ?? null;
    const previous = previousTenant.current;
    previousTenant.current = current;
    if (!current || !previous) return;
    // Case-insensitive: lo stesso tenant torna maiuscolo o minuscolo secondo l'endpoint, e un
    // confronto esatto qui butterebbe la cache a ogni refresh del token.
    if (current.toLowerCase() === previous.toLowerCase()) return;
    qc.clear();
  }, [state.decoded?.tenantId, qc]);

  useEffect(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    if (!state.token) return;
    const ms = msUntilExpiry(state.token) - REFRESH_BUFFER_MS;
    if (ms <= 0) {
      void doRefresh();
      return;
    }
    // Clamp to MAX_SAFE_TIMEOUT to prevent setTimeout overflow firing immediately
    // when the JWT expiry is far in the future (BE issues ~30-day tokens).
    const delay = Math.min(ms, MAX_SAFE_TIMEOUT);
    refreshTimer.current = setTimeout(() => {
      void doRefresh();
    }, delay);
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [state.token, doRefresh]);

  useEffect(() => {
    if (!state.token && state.refreshToken) {
      void doRefresh();
    }
    void ensureFingerprint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Ripiego per la finestra fra il primo render di un reload e la risposta del refresh: in quel
   * tratto `decoded` è `null` e non sappiamo che permessi ha chi guarda. Si legge dallo storage
   * **solo** in quella finestra, e diventa `[]` appena il token è decodificato — così un logout,
   * che cancella la chiave, non lascia in giro i claim di chi c'era prima.
   */
  const claimsPersisted = useMemo(
    () => (state.decoded ? [] : loadPersistedClaims()),
    [state.decoded],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login: doLogin,
      loginWithEnvelope: applyAuthResponse,
      changePasswordAndLogin,
      logout: doLogout,
      refresh: doRefresh,
      isAuthenticated: Boolean(state.token),
      /*
       * I claim della sessione, con la copia persistita come ripiego finché il refresh non
       * risponde. Chi disegna il menu legge **questo** e non `decoded.claims`: su un reload
       * quest'ultimo è `null` per qualche centinaio di millisecondi, e in quella finestra il menu
       * si mostrerebbe intero.
       */
      claims: state.decoded?.claims ?? claimsPersisted,
      readyClaims: Boolean(state.decoded) || claimsPersisted.length > 0,
      needsTenantSelection: Boolean(state.token) && state.associatedTenants.length > 1,
    }),
    [state, claimsPersisted, doLogin, applyAuthResponse, changePasswordAndLogin, doLogout, doRefresh]
  );

  // Anche sul reload, prima del refresh: i claim persistiti dicono che qualcuno è entrato.
  setSessionPresent(Boolean(state.token) || claimsPersisted.length > 0);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook colocated with the provider for ergonomics; not a Fast-Refresh issue in practice.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Come `useAuth`, ma `null` fuori dal provider invece di un errore. Serve ai pannelli condivisi che
 * decidono **cosa offrire** in base a chi guarda e si montano anche dove una sessione non c'è
 * (le prove, un'anteprima): lì «non so chi sei» vuol dire «non ti offro i comandi riservati».
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
